import { useState, useEffect } from 'react';
import './UserProfilePage.css';
import axiosInstance from '../../utils/axios';
import { Navigate } from 'react-router-dom';

const UserProfilePage = () => {
    const [user, setUser] = useState({});
    const [redirect, setRedirect] = useState(false);
    const [paymentLoading, setPaymentLoading] = useState(false);
    const [notificationLoading, setEitificationLoading] = useState(false);
    const [amount, setAmount] = useState('');
    const [transactions, setTransactions] = useState([]);

    useEffect(() => {
        const userId = localStorage.getItem('userId');
        if (!userId) {
            setRedirect(true);
            return;
        }
        Promise.all([
            axiosInstance.get(`/api/private/user/${userId}`),
            axiosInstance.get('/api/private/transactions/me')
        ])
        .then(([userResponse, transactionResponse]) => {
            setUser(userResponse.data);
            setTransactions(Array.isArray(transactionResponse?.data) ? transactionResponse.data : []);
        })
        .catch(err => {
            console.log('Viga kasutaja andmete laadimisel:', err);
        });
    }, []);

    const handleAddFunds = async (e) => {
        e.preventDefault();

        const amountValue = parseFloat(amount);
        if (isNaN(amountValue) || amountValue <= 0) {
            alert('Sisesta sobiv summa, mis on suurem kui 0');
            return;
        }

        try {
            const userId = user.id || localStorage.getItem('userId');
            const response = await axiosInstance.post('/api/private/payments/create', {
                amount: amountValue,
                currency: 'EUR',
                orderReference: `order-${Date.now()}-${userId}`,
                email: user.email,
                processingAccount: 'EUR3D1'
            });

            console.log('Payment response:', response.data);

            window.location.href = response.data.payment_link;
        } catch (error) {
            console.error('Makse alustamine ebaõnnestus', error);
            const errorMessage = error.response?.data?.error ||
                                'Makse alustamine ei läinud läbi, proovi uuesti.';
            alert(`Makse viga: ${errorMessage}`);
        } finally {
            setPaymentLoading(false);
        }
    };

    const handleEitificationToggle = async (e) => {
        const enabled = e.target.checked;
        const userId = user.id || localStorage.getItem('userId');
        if (!userId) {
            return;
        }

        const previousValue = Boolean(user.automaticNotificationsEnabled);
        setUser((prevUser) => ({ ...prevUser, automaticNotificationsEnabled: enabled }));
        setEitificationLoading(true);

        try {
            const response = await axiosInstance.put(
                `/api/private/users/${userId}/automatic-notifications`,
                JSON.stringify(enabled),
                { headers: { 'Content-Type': 'application/json' } }
            );
            setUser((prevUser) => ({
                ...prevUser,
                ...response.data,
                automaticNotificationsEnabled: response.data?.automaticNotificationsEnabled ?? enabled,
            }));
        } catch (error) {
            setUser((prevUser) => ({ ...prevUser, automaticNotificationsEnabled: previousValue }));
            alert('Proovi uuesti!');
        } finally {
            setEitificationLoading(false);
        }
    };

    if (redirect) {
        return <Navigate to="/unauthorized" replace />;
    }

    return (
        <div className="profile-container">
            <div className="profile-card">
                <div className="profile-header">
                    <h1>Profiil</h1>
                </div>

                <div className="profile-content">
                    <div className="info-section">
                        <h3>Konto info</h3>
                        <div className="info-grid">
                            <div className="info-item">
                                <label>Kasutajanimi:</label>
                                <span className="info-value">                                {user.username || 'Puudub'}</span>
                            </div>
                            <div className="info-item">
                                <label>Roll:</label>
                                <span className={`info-value role-badge ${user.role?.toLowerCase()}`}>
                                    {user.role || 'Kasutaja'}
                                </span>
                            </div>
                            <div className="info-item">
                                <label>Konto jääk:</label>
                                <span className="info-value balance">
                                    {user.balance?.toFixed(2) || '0.00'} €
                                </span>
                            </div>
                        </div>

                        <div className="add-funds-section">
                            <label>Lisa raha kontole</label>
                            <form onSubmit={handleAddFunds}>
                                <div className="amount-input-group">
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0.01"
                                        value={amount}
                                        onChange={(e) => setAmount(e.target.value)}
                                        placeholder="Sisesta summa"
                                        disabled={paymentLoading}
                                        className="amount-input"
                                    />
                                    <button
                                        type="submit"
                                        disabled={paymentLoading || !amount}
                                        className="add-funds-btn"
                                    >
                                        Mine maksma
                                    </button>
                                </div>
                                {paymentLoading && (
                                    <div className="payment-loading">
                                        Makseportaali avamine...
                                    </div>
                                )}
                            </form>
                        </div>
                    </div>

                    <div className="info-section">
                        <h3>Personaalne informatsioon</h3>
                        <div className="info-grid">
                            <div className="info-item">
                                <label>Eesnimi:</label>
                                <span className="info-value">                                {user.firstName || 'Puudub'}</span>
                            </div>
                            <div className="info-item">
                                <label>Perekonnanimi:</label>
                                <span className="info-value">                                {user.lastName || 'Puudub'}</span>
                            </div>
                            <div className="info-item">
                                <label>Meil:</label>
                                <span className="info-value">                                {user.email || 'Puudub'}</span>
                            </div>
                        </div>

                        <div className="notification-setting">
                            <label className="checkbox-row">
                                <input
                                    type="checkbox"
                                    checked={Boolean(user.automaticNotificationsEnabled)}
                                    onChange={handleEitificationToggle}
                                    disabled={notificationLoading}
                                />
                                <span>Tahan automaatseid teavitusi</span>
                            </label>
                        </div>
                    </div>

                    <div className="info-section transaction-section">
                        <h3>Tehingute ajalugu</h3>
                        {transactions.length === 0 ? (
                            <p>Tehinguid ei ole.</p>
                        ) : (
                            <div className="transaction-list">
                                {transactions.map((transaction) => (
                                    <div className="transaction-row" key={transaction.id}>
                                        <div>
                                            <strong>{transaction.description}</strong>
                                            <small>{new Date(transaction.createdAt).toLocaleString('et-EE')}</small>
                                        </div>
                                        <span className={transaction.amount >= 0 ? 'transaction-positive' : 'transaction-negative'}>
                                            {transaction.amount >= 0 ? '+' : ''}{Number(transaction.amount).toFixed(2)} €
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserProfilePage;
